import type { ReactNode, ChangeEvent, FocusEvent, ComponentProps } from 'react';
import classnames from 'classnames/bind';
import { isEmpty, isNil, isString, pipe } from 'lodash/fp';
import { Defaults } from 'app/lib/constants';
import { useDefaultId, useEventCallback } from 'app/lib/hooks';
import { useTheme } from 'app/components/core/ThemeProvider';
import { FieldLabel } from 'app/components/core/FieldLabel';
import { useErrorTooltip } from 'app/components/core/ErrorTooltip';
import { Icon, IconSize } from 'app/components/core/Icon';
import { Loading, LoadingSize } from 'app/components/core/Loading';
import * as styles from './Select.css';

const cx = classnames.bind(styles);

export enum SelectVariant {
    OUTLINE = 'outline',
    FILL = 'fill'
}

export enum SelectSize {
    SMALL = 'small',
    MEDIUM = 'medium',
    LARGE = 'large',
    EXTRA_LARGE = 'extra-large'
}

const VARIANT_CLASSES: Record<SelectVariant, string> = {
    [SelectVariant.OUTLINE]: 'variant-outline',
    [SelectVariant.FILL]: 'variant-fill'
};

const SIZE_CLASSES: Record<SelectSize, string> = {
    [SelectSize.SMALL]: 'size-sm',
    [SelectSize.MEDIUM]: 'size-md',
    [SelectSize.LARGE]: 'size-lg',
    [SelectSize.EXTRA_LARGE]: 'size-xl'
};

const LOADING_SIZE: Record<SelectSize, LoadingSize> = {
    [SelectSize.SMALL]: LoadingSize.EXTRA_SMALL,
    [SelectSize.MEDIUM]: LoadingSize.EXTRA_SMALL,
    [SelectSize.LARGE]: LoadingSize.SMALL,
    [SelectSize.EXTRA_LARGE]: LoadingSize.MEDIUM
};

interface SelectOption {
    value: string;
    label?: string;
    disabled?: boolean;
}

type SelectOptions = string[] | SelectOption[];

const castOption = (option: string | SelectOption): SelectOption =>
    isString(option) ? { value: option, label: option } : option;

const renderOption = ({ value, label = value, disabled }: SelectOption) => (
    <option key={value} value={value} disabled={disabled}>
        {label}
    </option>
);

export interface SelectProps extends Omit<
    ComponentProps<'select'>,
    'size' | 'onChange' | 'onBlur'
> {
    testId?: string;
    className?: string;
    options?: SelectOptions;
    name?: string;
    placeholder?: string;
    id?: string;
    label?: ReactNode;
    value?: string;
    onChange?: (value: string) => void;
    onBlur?: (value: string) => void;
    disabled?: boolean;
    error?: string | string[];
    variant?: SelectVariant;
    size?: SelectSize;
    isProcessing?: boolean;
}

export function Select({
    testId,
    className,
    options = Defaults.ARRAY,
    id: suppliedId,
    name,
    placeholder,
    value,
    label,
    onChange = Defaults.NOOP,
    onBlur = Defaults.NOOP,
    disabled = false,
    error,
    variant = SelectVariant.OUTLINE,
    size = SelectSize.LARGE,
    isProcessing = false,
    ...props
}: SelectProps) {
    const id = useDefaultId('select', suppliedId);
    const handleChange = useEventCallback(onChange);
    const handleBlur = useEventCallback(onBlur);
    const { themeClassname } = useTheme();
    const classes = cx(
        'base',
        themeClassname,
        VARIANT_CLASSES[variant],
        SIZE_CLASSES[size],
        className
    );

    const selectClasses = cx('select', { isProcessing });

    const hasError = Boolean(error?.length);

    const { ErrorTooltip, getTooltipProps, getReferenceProps } = useErrorTooltip();

    return (
        <div className={classes} data-testid={testId}>
            <FieldLabel htmlFor={id}>{label}</FieldLabel>
            <div className={styles.wrapper}>
                <select
                    autoComplete="off"
                    id={id}
                    name={name || undefined}
                    className={selectClasses}
                    value={isNil(value) ? '' : value}
                    data-is-empty={isEmpty(value)}
                    onChange={handleChange as (e: ChangeEvent<HTMLSelectElement>) => void}
                    disabled={isProcessing || disabled}
                    aria-invalid={hasError}
                    {...getReferenceProps({
                        onBlur: handleBlur as (e: FocusEvent<HTMLSelectElement>) => void,
                        ...props
                    })}>
                    {isNil(value) && (
                        <option value="" disabled>
                            {placeholder}
                        </option>
                    )}
                    {options?.map(pipe(castOption, renderOption))}
                </select>
                <span className={styles.icons}>
                    {isProcessing ? (
                        <Loading size={LOADING_SIZE[size]} />
                    ) : (
                        <Icon
                            className={styles.arrow}
                            variant="dropdown-open"
                            size={IconSize.EXTRA_SMALL}
                        />
                    )}
                </span>
                <ErrorTooltip error={error} {...getTooltipProps()} />
            </div>
        </div>
    );
}
